import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { useAuth } from "./auth";
import { useToast } from "./toast";

const NotifCtx = createContext(null);

// Ask once for browser notification permission (used when prefs.browser is on).
export function requestBrowserPermission() {
  if (!("Notification" in window)) return Promise.resolve("unsupported");
  if (Notification.permission !== "default") return Promise.resolve(Notification.permission);
  return Notification.requestPermission();
}

function showBrowserNotification(n) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    new Notification(n.title, { body: n.body || "", tag: `tf-${n.id}` });
  } catch { /* ignore */ }
}

// "HH:MM" strings compared against the browser's local clock — handles
// overnight ranges (e.g. 20:00–07:00) by wrapping past midnight.
function isQuietHours(prefs) {
  if (!prefs.quiet_enabled || !prefs.quiet_start || !prefs.quiet_end) return false;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = prefs.quiet_start.split(":").map(Number);
  const [eh, em] = prefs.quiet_end.split(":").map(Number);
  const start = sh * 60 + sm, end = eh * 60 + em;
  return start <= end ? (cur >= start && cur < end) : (cur >= start || cur < end);
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const { push } = useToast();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wsRef = useRef(null);

  const load = useCallback(() => {
    if (!user) return;
    // limit alto: si el total no-leído (unread) supera los 30 items de siempre,
    // el badge parece "no borrarse nunca" porque las de atrás del backlog no
    // llegan a mostrarse — con más margen, marcar las visibles sí baja el conteo.
    api.get("/notifications", { params: { limit: 100 } }).then((r) => {
      setItems(r.data.items);
      setUnread(r.data.unread);
    }).catch(() => {});
  }, [user]);

  // Carga inicial + POLLING de respaldo: si el WebSocket no entrega (proxy que
  // no hace upgrade, caídas), igual aparecen las notificaciones nuevas cada 45s.
  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 45000);
    const onFocus = () => load();               // y al volver a la pestaña
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user, load]);

  useEffect(() => {
    const refresh = () => load();
    window.addEventListener("tf:notifications-refresh", refresh);
    return () => window.removeEventListener("tf:notifications-refresh", refresh);
  }, [load]);

  // Live notifications over the per-user WebSocket (dept channel carries them too).
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("tf_token");
    if (!token) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const dept = user.department_id ?? 0;
    const url = `${proto}://${location.host}/ws/${dept}?token=${token}`;
    let closed = false, retry;
    const connect = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        let msg; try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.event === "notification") {
          const n = msg.data;
          window.dispatchEvent(new CustomEvent("tf:notification", { detail: n }));
          setItems((prev) => [n, ...prev].slice(0, 30));
          setUnread((u) => u + 1);
          const prefs = safePrefs(user);
          // During quiet hours the badge/list still update — just no popup/toast.
          if (!isQuietHours(prefs)) {
            push(`${n.title}${n.body ? " · " + n.body : ""}`, "info");
            if (prefs.browser) showBrowserNotification(n);
            if (safeUiPrefs(user).notif_sound) playBeep();
          }
        }
      };
      ws.onclose = () => { if (!closed) retry = setTimeout(connect, 3000); };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); wsRef.current?.close(); };
  }, [user, push]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    await api.post(`/notifications/${id}/read`).catch(() => {});
  };
  const markAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
    await api.post("/notifications/read-all").catch(() => {});
  };

  return (
    <NotifCtx.Provider value={{ items, unread, load, markRead, markAll }}>
      {children}
    </NotifCtx.Provider>
  );
}

function safeUiPrefs(user) {
  try { return user?.ui_prefs ? JSON.parse(user.ui_prefs) : {}; }
  catch { return {}; }
}

// Beep corto para el aviso sonoro de notificaciones (WebAudio, sin archivo).
function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "sine"; o.frequency.value = 880;
    g.gain.value = 0.05;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.15);
    setTimeout(() => ctx.close(), 300);
  } catch { /* ignore */ }
}

function safePrefs(user) {
  try { return user?.notify_prefs ? JSON.parse(user.notify_prefs) : { browser: true }; }
  catch { return { browser: true }; }
}

export const useNotifications = () => useContext(NotifCtx);
